import { useState } from "react";
import { Calculator, Phone, AlertTriangle } from "lucide-react";

const STUMP_SIZES = [
  { label: 'Small (under 12")', value: "small", min: 300, max: 425 },
  { label: 'Medium (12–24")', value: "medium", min: 470, max: 720 },
  { label: 'Large (24–36")', value: "large", min: 650, max: 825 },
  { label: 'Extra Large (36"+)', value: "xlarge", min: 750, max: 850 },
];

const ACCESS_OPTIONS = [
  { label: "Easy — open yard, driveway access", value: "easy", adj: 0 },
  { label: "Moderate — gate or side yard", value: "moderate", adj: 40 },
  { label: "Difficult — steep slope or tight space", value: "difficult", adj: 85 },
];

const QUANTITY_OPTIONS = [
  { label: "1 Stump", value: 1, discount: 0 },
  { label: "2–3 Stumps", value: 2, discount: 0.05 },
  { label: "4+ Stumps", value: 4, discount: 0.10 },
];

const PriceEstimator = () => {
  const [size, setSize] = useState(null);
  const [access, setAccess] = useState(null);
  const [quantity, setQuantity] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const allSelected = size !== null && access !== null && quantity !== null;

  const calculate = () => {
    if (!allSelected) return null;
    const s = STUMP_SIZES[size];
    const a = ACCESS_OPTIONS[access];
    const q = QUANTITY_OPTIONS[quantity];

    let low = s.min + a.adj;
    let high = s.max + a.adj;

    // quantity discount
    low = Math.round(low * (1 - q.discount));
    high = Math.round(high * (1 - q.discount));

    // per-stump price × count (cap at actual count for display)
    const count = q.value === 4 ? 4 : q.value;
    return { low, high, perStump: true, count };
  };

  const handleEstimate = () => {
    if (allSelected) setShowResult(true);
  };

  const result = calculate();

  const OptionButton = ({ selected, onClick, children, testId }) => (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border-2 transition-all font-body text-sm ${
        selected
          ? "border-[#FF4D00] bg-[#FF4D00]/10 text-[#1A1A1A] font-semibold"
          : "border-gray-200 bg-white text-gray-700 hover:border-gray-400"
      }`}
    >
      {children}
    </button>
  );

  return (
    <section className="py-16 lg:py-24 bg-[#F5F5F0]" id="price-estimator">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left — Estimator Form */}
          <div>
            <div className="accent-line mb-4" />
            <div className="flex items-center gap-3 mb-2">
              <Calculator className="w-6 h-6 text-[#FF4D00]" />
              <p className="font-heading text-sm font-bold text-[#FF4D00] uppercase tracking-wider">
                Ballpark Estimate Tool
              </p>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1A1A] uppercase mb-2 leading-tight">
              What Might Stump<br />Grinding Cost Me?
            </h2>
            <p className="font-body text-gray-500 text-sm mb-8">
              Answer 3 quick questions for a rough price range. This is <span className="font-bold underline">not a quote</span> — every stump is different. Call us for an exact price.
            </p>

            {/* Step 1: Size */}
            <div className="mb-6">
              <p className="font-heading text-sm font-bold text-[#1A1A1A] uppercase tracking-wider mb-3">
                1. Approximate Stump Diameter
              </p>
              <div className="grid grid-cols-2 gap-2">
                {STUMP_SIZES.map((s, i) => (
                  <OptionButton
                    key={s.value}
                    selected={size === i}
                    onClick={() => { setSize(i); setShowResult(false); }}
                    testId={`size-${s.value}`}
                  >
                    {s.label}
                  </OptionButton>
                ))}
              </div>
            </div>

            {/* Step 2: Accessibility */}
            <div className="mb-6">
              <p className="font-heading text-sm font-bold text-[#1A1A1A] uppercase tracking-wider mb-3">
                2. Stump Accessibility
              </p>
              <div className="grid gap-2">
                {ACCESS_OPTIONS.map((a, i) => (
                  <OptionButton
                    key={a.value}
                    selected={access === i}
                    onClick={() => { setAccess(i); setShowResult(false); }}
                    testId={`access-${a.value}`}
                  >
                    {a.label}
                  </OptionButton>
                ))}
              </div>
            </div>

            {/* Step 3: Quantity */}
            <div className="mb-8">
              <p className="font-heading text-sm font-bold text-[#1A1A1A] uppercase tracking-wider mb-3">
                3. How Many Stumps?
              </p>
              <div className="grid grid-cols-3 gap-2">
                {QUANTITY_OPTIONS.map((q, i) => (
                  <OptionButton
                    key={q.value}
                    selected={quantity === i}
                    onClick={() => { setQuantity(i); setShowResult(false); }}
                    testId={`qty-${q.value}`}
                  >
                    {q.label}
                  </OptionButton>
                ))}
              </div>
            </div>

            <button
              data-testid="estimate-btn"
              onClick={handleEstimate}
              disabled={!allSelected}
              className={`w-full py-4 font-heading font-bold uppercase text-lg tracking-wider transition-all ${
                allSelected
                  ? "bg-[#FF4D00] text-white hover:bg-[#e64400] cursor-pointer"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              See My Estimate
            </button>
          </div>

          {/* Right — Result Card */}
          <div className="lg:sticky lg:top-24">
            {!showResult ? (
              <div className="bg-[#1A1A1A] border-2 border-gray-700 p-10 text-center">
                <Calculator className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="font-heading text-xl font-bold text-gray-500 uppercase">
                  Your Estimate
                </p>
                <p className="font-body text-sm text-gray-600 mt-2">
                  Select your options to see a ballpark range
                </p>
              </div>
            ) : result && (
              <div className="bg-[#1A1A1A] border-2 border-[#FF4D00] overflow-hidden" data-testid="estimate-result">
                {/* Result Header */}
                <div className="bg-[#FF4D00] px-6 py-4">
                  <p className="font-heading font-bold text-white uppercase text-sm tracking-wider text-center">
                    Estimated Price Range (Per Stump)
                  </p>
                </div>

                {/* Price Display */}
                <div className="px-6 py-8 text-center">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <span className="font-heading text-5xl font-extrabold text-white">
                      ${result.low}
                    </span>
                    <span className="font-heading text-2xl text-gray-500">–</span>
                    <span className="font-heading text-5xl font-extrabold text-[#FF4D00]">
                      ${result.high}
                    </span>
                  </div>

                  {result.count > 1 && (
                    <p className="font-body text-sm text-green-400 mt-2">
                      Multi-stump discount applied ({QUANTITY_OPTIONS[quantity].discount * 100}% off per stump)
                    </p>
                  )}
                </div>

                {/* Disclaimer */}
                <div className="mx-6 mb-6 bg-yellow-900/30 border border-yellow-700/50 px-4 py-3 flex gap-3 items-start">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <p className="font-body text-xs text-yellow-200/80 leading-relaxed">
                    <span className="font-bold text-yellow-300">This is NOT a quote.</span> Actual pricing depends on stump species, root depth, soil conditions, and obstacles. This tool provides a rough ballpark only. Call for a free, no-obligation exact price.
                  </p>
                </div>

                {/* CTA */}
                <div className="px-6 pb-6">
                  <a
                    href="tel:+15202007739,2"
                    data-testid="estimator-call-btn"
                    className="block w-full bg-[#FF4D00] hover:bg-[#e64400] text-white text-center py-4 font-heading font-bold uppercase text-lg tracking-wider transition-colors"
                  >
                    <Phone className="w-5 h-5 inline-block mr-2 -mt-1" />
                    Call for Exact Price
                  </a>
                  <p className="font-body text-xs text-gray-500 text-center mt-3">
                    (520) 200-7739 Ext 2 — Free estimates, no obligation
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PriceEstimator;
